import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Calculator,
  Calendar,
  Edit3,
  FileText,
  Heart,
  Pill,
  Plus,
  Ruler,
  Save,
  Scale,
  Shield,
  Trash2,
  TrendingUp,
  User,
  Utensils,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { BodyDataChart } from "../../components/charts/BodyDataChart";
import { EmotionalWellbeingBox } from "../../components/health/EmotionalWellbeingBox";
import { ExtendedAllergies } from "../../components/health/ExtendedAllergies";
import { HealthEcosystemImportBox } from "../../components/health/HealthEcosystemImportBox";
import { LabResults } from "../../components/health/LabResults";
import { PhysicalWellbeingBox } from "../../components/health/PhysicalWellbeingBox";
import { VitalSignsBox } from "../../components/health/VitalSignsBox";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  BodyDataHistoryItem,
  bodyDataService,
} from "../../services/bodyData.service";
import { healthDataService } from "../../services/healthData.service";
import { labResultsService } from "../../services/labResults.service";
import {
  WellbeingHistoryPoint,
  wellbeingService,
} from "../../services/wellbeing.service";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import { HealthDataForm, MedicalIncident } from "../../types/fhir-health";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import {
  cmToFeetInches,
  feetInchesToCm,
  inToCm,
  lbToKg,
} from "../../utils/unitLocalization";
import { i18nToast } from "../../utils/i18nToast";
// Phase 2: Medication & Treatment History
// import { MedicationManagement } from '../../components/health/MedicationManagement'; // Commented out - Box hidden from UI
import { MedicalConditions } from "../../components/health/MedicalConditions";
import { MedicalProceduresComponent } from "../../components/health/MedicalProcedures";
import { TreatmentHistoryComponent } from "../../components/health/TreatmentHistory";
import { log } from "@/lib/log";

type ChartRange = "7d" | "30d" | "90d" | "1y" | "all";

const CHART_RANGES: {
  value: ChartRange;
  labelKey:
    | "hrpRange7d"
    | "hrpRange30d"
    | "hrpRange90d"
    | "hrpRange1y"
    | "hrpRangeAll";
  days: number | null;
}[] = [
  { value: "7d", labelKey: "hrpRange7d", days: 7 },
  { value: "30d", labelKey: "hrpRange30d", days: 30 },
  { value: "90d", labelKey: "hrpRange90d", days: 90 },
  { value: "1y", labelKey: "hrpRange1y", days: 365 },
  { value: "all", labelKey: "hrpRangeAll", days: null },
];

/** Stored weight in kg, whatever unit an older record was saved with. */
function storedWeightKg(weight?: { value: number; unit: string }): number {
  if (!weight?.value) return 0;
  return weight.unit === "lb" ? lbToKg(weight.value) : weight.value;
}

/** Stored height in cm, whatever unit an older record was saved with. */
function storedHeightCm(height?: { value: number; unit: string }): number {
  if (!height?.value) return 0;
  return height.unit === "in" ? inToCm(height.value) : height.value;
}

function filterByRange(
  history: BodyDataHistoryItem[],
  range: ChartRange,
): BodyDataHistoryItem[] {
  const days = CHART_RANGES.find((r) => r.value === range)?.days;
  if (!days) return history;

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return history.filter((entry) => {
    const at = new Date(entry.timestamp).getTime();
    // Keep entries with an unparseable timestamp rather than silently
    // dropping data the user did record.
    return Number.isNaN(at) || at >= cutoff;
  });
}

export const HealthRecordsPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [healthData, setHealthData] = useState<HealthDataForm>({
    gender: "",
    bloodType: "",
    weight: { value: 0, unit: "kg" },
    height: { value: 0, unit: "cm" },
    bodyFat: { value: 0, unit: "%" },
    bloodPressure: {
      systolic: 120,
      diastolic: 80,
      unit: "mmHg",
      recordedAt: new Date().toISOString(),
    },
    restingHeartRate: {
      value: 70,
      unit: "bpm",
      recordedAt: new Date().toISOString(),
    },
    physicalWellbeing: 3,
    emotionalWellbeing: 3,
    wellbeingRecorded: new Date().toISOString(),
    wellbeingScore: 3, // Legacy field
    currentComplaints: "",
    medicalIncidents: [],
    foodIntolerances: [],
    medicationAllergies: [],
    coagulationDisorder: false,
    heartAttack: { hasHad: false },
    stroke: { hasHad: false },
    // Phase 1: Critical Travel Health Data
    immunizations: [],
    labResults: [],
    extendedAllergies: [],

    // Phase 2: Medication & Treatment History
    currentMedications: [],
    medicalConditions: [],
    medicalProcedures: [],
    treatmentHistory: [],

    // Phase 3: Emergency Preparedness & Travel Health
    emergencyContacts: [],
    familyHistory: [],
    travelHealthData: [],
    extendedVitalSigns: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  // Individual section editing states
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [savingSections, setSavingSections] = useState<Record<string, boolean>>(
    {},
  );

  // Wellbeing history state
  const [wellbeingHistory, setWellbeingHistory] = useState<
    WellbeingHistoryPoint[]
  >([]);

  // Body data history state
  const [chartRange, setChartRange] = useState<ChartRange>("all");
  const [bodyDataHistory, setBodyDataHistory] = useState<BodyDataHistoryItem[]>(
    [],
  );
  const [lastWeightUpdate, setLastWeightUpdate] = useState<string>("");

  // Input drafts in the display unit (lb / ft+in for English, kg / cm
  // otherwise). Storage stays kg and cm; the drafts are converted on save.
  const [weightDraft, setWeightDraft] = useState("");
  const [heightDraft, setHeightDraft] = useState({ cm: "", ft: "", in: "" });
  // The drafts as they were opened. A draft nobody changed is not converted
  // back: 70 kg shows as 154.3 lb, and 154.3 lb is 69.99 kg — saving body fat
  // alone used to write that as a new weight observation.
  const [weightDraftOpened, setWeightDraftOpened] = useState("");
  const [heightDraftOpened, setHeightDraftOpened] = useState({
    cm: "",
    ft: "",
    in: "",
  });

  // Load wellbeing history
  const loadWellbeingHistory = async () => {
    try {
      const history = await wellbeingService.getWellbeingHistory();
      setWellbeingHistory(history);
    } catch (error: any) {
      console.error("Failed to load wellbeing history:", error);
    }
  };

  // Load body data history
  const loadBodyDataHistory = async () => {
    // Check if user is authenticated
    if (!user || !token) {
      return;
    }

    try {
      // ✅ CRITICAL: Only load REAL FHIR data from backend
      // NO mock data generation - only actual user entries from database
      const history = await bodyDataService.getBodyDataHistory(12);

      if (history && history.length > 0) {
        setBodyDataHistory(history);
        // Get the most recent timestamp for last update display
        const sortedByTime = [...history].sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
        setLastWeightUpdate(
          sortedByTime[0]?.timestamp || new Date().toISOString(),
        );
      } else {
        // No real data exists - show empty state, not fake data
        setBodyDataHistory([]);
        setLastWeightUpdate("");
      }
    } catch (error: any) {
      console.error("❌ Failed to load body data history:", error);
      // On error, show empty state - NEVER show mock data
      setBodyDataHistory([]);
      setLastWeightUpdate("");
    }
  };

  // Save physical wellbeing only
  const handleSavePhysicalWellbeing = async () => {
    try {
      await wellbeingService.saveWellbeingAssessment(
        healthData.physicalWellbeing,
        healthData.emotionalWellbeing,
      );

      // Reload latest values and history
      await loadLatestWellbeingValues();
      await loadWellbeingHistory();

      i18nToast.success(t.wellbSaved);
      log.debug("✅ Physical wellbeing saved successfully");
    } catch (error: any) {
      console.error("❌ Failed to save physical wellbeing:", error);
      i18nToast.error(t.wellbSaveFailed);
    }
  };

  // Save emotional wellbeing only
  const handleSaveEmotionalWellbeing = async () => {
    try {
      await wellbeingService.saveWellbeingAssessment(
        healthData.physicalWellbeing,
        healthData.emotionalWellbeing,
      );

      // Reload latest values and history
      await loadLatestWellbeingValues();
      await loadWellbeingHistory();

      i18nToast.success(t.wellbSaved);
      log.debug("✅ Emotional wellbeing saved successfully");
    } catch (error: any) {
      console.error("❌ Failed to save emotional wellbeing:", error);
      i18nToast.error(t.wellbSaveFailed);
    }
  };

  // ✅ NEW: Load latest wellbeing values from FHIR database
  const loadLatestWellbeingValues = async () => {
    try {
      const latestData = await wellbeingService.getLatestWellbeing();

      setHealthData((prev) => ({
        ...prev,
        physicalWellbeing: latestData.physicalWellbeing,
        emotionalWellbeing: latestData.emotionalWellbeing,
        wellbeingRecorded: latestData.recordedAt,
      }));

      log.debug("✅ Latest wellbeing values loaded:", latestData);
    } catch (error: any) {
      console.error("❌ Failed to load latest wellbeing values:", error);
    }
  };

  // Form states
  const [newIncident, setNewIncident] = useState<Partial<MedicalIncident>>({
    date: "",
    diagnosis: "",
    icdCode: "",
    description: "",
    documents: [],
  });
  const [newFoodIntolerance, setNewFoodIntolerance] = useState("");
  const [newMedicationAllergy, setNewMedicationAllergy] = useState(""); // Preserved for data consistency

  useEffect(() => {
    loadHealthData();
    loadLatestWellbeingValues(); // ✅ NEW: Load latest wellbeing values
    loadWellbeingHistory();
    loadBodyDataHistory();
    checkDataExpiration();
    loadLabResults();
  }, []);

  // Load body data when user/token becomes available
  useEffect(() => {
    if (user && token) {
      log.debug("🔄 User and token available, loading body data...");
      loadBodyDataHistory();
    }
  }, [user, token]);

  useEffect(() => {
    // Calculate BMI when weight or height changes
    if (healthData.weight.value && healthData.height.value) {
      calculateBMI();
    }

    // Reload body data history when weight changes
    if (healthData.weight.value > 0) {
      loadBodyDataHistory();
    }
  }, [healthData.weight, healthData.height]);

  useEffect(() => {
    // Update age when user data is available
    if (user?.dateOfBirth) {
      const calculatedAge = calculateAge(user.dateOfBirth);
      setHealthData((prev) => ({ ...prev, age: calculatedAge }));
    }
  }, [user?.dateOfBirth]);

  const loadHealthData = async () => {
    if (!user?.id || !token) return;

    try {
      setLoading(true);
      const data = await healthDataService.getHealthData(user.id, token);
      if (data) {
        const calculatedAge = user.dateOfBirth
          ? calculateAge(user.dateOfBirth)
          : 0;
        setHealthData({
          ...data,
          age: calculatedAge,
        });
      }
    } catch (err: any) {
      setError(t.hrpErrorLoading);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLabResults = async () => {
    if (!user?.id || !token) return;

    try {
      log.debug("🧪 Loading lab results from FHIR database...");
      const labResultsResponse = await labResultsService.getLabResults();
      if (labResultsResponse.success && labResultsResponse.data) {
        log.debug("✅ Lab results loaded:", labResultsResponse.data);
        setHealthData((prev) => ({
          ...prev,
          labResults: labResultsResponse.data || [],
        }));
      } else {
        log.debug("ℹ️ No lab results found or error loading");
      }
    } catch (err: any) {
      console.error("❌ Error loading lab results:", err);
    }
  };

  const checkDataExpiration = async () => {
    if (!user?.id || !token) return;

    try {
      const result: any = await healthDataService.checkDataExpiration(
        user.id,
        token,
      );
      setNeedsConfirmation(result.needsUpdate);
    } catch (err: any) {
      console.error("Error checking data expiration:", err);
    }
  };

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };

  const calculateBMI = () => {
    if (!healthData.weight.value || !healthData.height.value) return;

    // Convert to metric
    const weightKg =
      healthData.weight.unit === "kg"
        ? healthData.weight.value
        : healthData.weight.value * 0.453592;
    const heightM =
      healthData.height.unit === "cm"
        ? healthData.height.value / 100
        : healthData.height.value * 0.0254;

    if (heightM === 0) return;

    const bmi = Math.round((weightKg / (heightM * heightM)) * 10) / 10;
    setHealthData((prev) => ({ ...prev, bmi }));
  };

  const parseLocalizedNumber = (value: string): number => {
    const normalized = value.replace(",", ".").trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const handleSaveSection = async (
    sectionKey: string,
    sectionLabel: string,
    dataOverride?: HealthDataForm,
  ) => {
    if (!user?.id || !token) return;

    try {
      setSavingSections((prev) => ({ ...prev, [sectionKey]: true }));
      setError(null);
      await healthDataService.saveHealthData(
        user.id,
        dataOverride ?? healthData,
        token,
      );
      alert(t.hrpSectionSaved.replace("{section}", sectionLabel));
      setEditingSection(null);
      await loadHealthData();
    } catch (err: any) {
      setError(t.hrpSectionSaveError.replace("{section}", sectionLabel));
      console.error(err);
    } finally {
      setSavingSections((prev) => ({ ...prev, [sectionKey]: false }));
    }
  };

  const toggleEditSection = (sectionName: string) => {
    const opening = editingSection !== sectionName;
    if (opening && sectionName === "basisdaten") {
      const cm = storedHeightCm(healthData.height);
      const { feet, inches } = cmToFeetInches(cm);
      const draft =
        cm > 0
          ? { cm: String(Math.round(cm)), ft: String(feet), in: String(inches) }
          : { cm: "", ft: "", in: "" };
      setHeightDraft(draft);
      setHeightDraftOpened(draft);
    }
    if (opening && sectionName === "koerperdaten") {
      const kg = storedWeightKg(healthData.weight);
      const draft = kg > 0 ? String(fmt.toDisplay("weight", kg)) : "";
      setWeightDraft(draft);
      setWeightDraftOpened(draft);
    }
    setEditingSection(opening ? sectionName : null);
  };

  /** The height draft in cm, or 0 when nothing usable was entered. */
  const heightDraftCm = (): number => {
    if (fmt.systemOf("height") === "imperial") {
      const feet = parseLocalizedNumber(heightDraft.ft);
      const inches = parseLocalizedNumber(heightDraft.in);
      if (feet <= 0 && inches <= 0) return 0;
      return Math.round(feetInchesToCm(feet, inches) * 10) / 10;
    }
    return parseLocalizedNumber(heightDraft.cm);
  };

  const saveBasicData = () => {
    const heightUnchanged =
      heightDraft.cm === heightDraftOpened.cm &&
      heightDraft.ft === heightDraftOpened.ft &&
      heightDraft.in === heightDraftOpened.in;
    const cm = heightUnchanged ? 0 : heightDraftCm();
    const updated: HealthDataForm = {
      ...healthData,
      height: cm > 0 ? { value: cm, unit: "cm" } : healthData.height,
    };
    setHealthData(updated);
    void handleSaveSection("basisdaten", t.hrpBasicData, updated);
  };

  const addMedicalIncident = () => {
    if (!newIncident.date || !newIncident.diagnosis) return;

    const incident: MedicalIncident = {
      id: Date.now().toString(),
      date: newIncident.date!,
      diagnosis: newIncident.diagnosis!,
      icdCode: newIncident.icdCode,
      description: newIncident.description,
      documents: newIncident.documents || [],
    };

    setHealthData((prev) => ({
      ...prev,
      medicalIncidents: [...prev.medicalIncidents, incident],
    }));

    setNewIncident({
      date: "",
      diagnosis: "",
      icdCode: "",
      description: "",
      documents: [],
    });
  };

  const removeMedicalIncident = (id: string) => {
    setHealthData((prev) => ({
      ...prev,
      medicalIncidents: prev.medicalIncidents.filter(
        (incident) => incident.id !== id,
      ),
    }));
  };

  const addFoodIntolerance = () => {
    if (!newFoodIntolerance.trim()) return;

    setHealthData((prev) => ({
      ...prev,
      foodIntolerances: [...prev.foodIntolerances, newFoodIntolerance.trim()],
    }));
    setNewFoodIntolerance("");
  };

  const removeFoodIntolerance = (index: number) => {
    setHealthData((prev) => ({
      ...prev,
      foodIntolerances: prev.foodIntolerances.filter((_, i) => i !== index),
    }));
  };

  // Medication functions - preserved for data consistency
  const addMedicationAllergy = () => {
    if (!newMedicationAllergy.trim()) return;

    setHealthData((prev) => ({
      ...prev,
      medicationAllergies: [
        ...prev.medicationAllergies,
        newMedicationAllergy.trim(),
      ],
    }));
    setNewMedicationAllergy("");
  };

  const removeMedicationAllergy = (index: number) => {
    setHealthData((prev) => ({
      ...prev,
      medicationAllergies: prev.medicationAllergies.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  // Suppress unused variable warnings - functions preserved for data consistency
  void addMedicationAllergy;
  void removeMedicationAllergy;

  const getBMICategory = (bmi: number): { category: string; color: string } => {
    if (bmi < 18.5)
      return { category: t.hrpBmiUnderweight, color: "text-blue-600" };
    if (bmi < 25) return { category: t.hrpBmiNormal, color: "text-green-600" };
    if (bmi < 30)
      return { category: t.hrpBmiOverweight, color: "text-yellow-600" };
    return { category: t.hrpBmiObesity, color: "text-red-600" };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <PageBanner
          icon={Heart}
          title={t.healthRecords}
          description={t.hrpBannerDescription}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={Heart}
        title={t.healthRecords}
        description={t.hrpBannerDescription}
      />
      {/* Data Expiration Warning */}
      {needsConfirmation && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <div>
              <h3 className="text-yellow-800 font-medium">
                {t.hrpConfirmRequiredTitle}
              </h3>
              <p className="text-yellow-700 text-sm">
                {t.hrpConfirmRequiredText}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Main Content */}
        <div className="space-y-6">
          {/* Basic Demographics */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <User className="w-5 h-5 text-sky-600" />
                <span>{t.hrpBasicData}</span>
              </h2>
              <div className="flex space-x-2">
                <button
                  data-testid="edit-basic-data"
                  onClick={() => toggleEditSection("basisdaten")}
                  className="p-2 text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                  title={editingSection === "basisdaten" ? t.cancel : t.edit}
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                {editingSection === "basisdaten" && (
                  <button
                    onClick={saveBasicData}
                    disabled={savingSections.basisdaten}
                    className="px-3 py-1 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
                  >
                    {savingSections.basisdaten ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    <span>{savingSections.basisdaten ? t.saving : t.save}</span>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  {t.hrpAgeCalculated}
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {healthData.age
                    ? `${healthData.age} ${t.years}`
                    : t.hrpNotAvailable}
                </p>
              </div>

              <div>
                <label
                  htmlFor="health-gender"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.gender}
                </label>
                {editingSection === "basisdaten" ? (
                  <select
                    id="health-gender"
                    value={healthData.gender}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        gender: e.target.value as any,
                      }))
                    }
                    className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    <option value="male">{t.male}</option>
                    <option value="female">{t.female}</option>
                    <option value="other">{t.hrpGenderDiverse}</option>
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">
                    {healthData.gender === "male"
                      ? t.male
                      : healthData.gender === "female"
                        ? t.female
                        : healthData.gender === "other"
                          ? t.hrpGenderDiverse
                          : t.notSpecified}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="health-blood-type"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.bloodType}
                </label>
                {editingSection === "basisdaten" ? (
                  <select
                    id="health-blood-type"
                    value={healthData.bloodType}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        bloodType: e.target.value,
                      }))
                    }
                    className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">
                    {healthData.bloodType || t.notSpecified}
                  </p>
                )}
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                  <Ruler className="w-4 h-4" />
                  <span>{t.height}</span>
                </div>
                {editingSection === "basisdaten" ? (
                  fmt.systemOf("height") === "imperial" ? (
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        name="heightFeet"
                        data-testid="height-input"
                        min="0"
                        aria-label={`${t.height} (ft)`}
                        value={heightDraft.ft}
                        onChange={(e) =>
                          setHeightDraft((prev) => ({
                            ...prev,
                            ft: e.target.value,
                          }))
                        }
                        className="w-0 flex-1 h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        placeholder="5"
                      />
                      <span className="flex items-center text-gray-600">
                        ft
                      </span>
                      <input
                        type="number"
                        name="heightInches"
                        data-testid="height-inches-input"
                        min="0"
                        max="11"
                        aria-label={`${t.height} (in)`}
                        value={heightDraft.in}
                        onChange={(e) =>
                          setHeightDraft((prev) => ({
                            ...prev,
                            in: e.target.value,
                          }))
                        }
                        className="w-0 flex-1 h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        placeholder="9"
                      />
                      <span
                        data-testid="height-unit"
                        className="flex items-center text-gray-600"
                      >
                        in
                      </span>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        name="height"
                        data-testid="height-input"
                        min="0"
                        aria-label={`${t.height} (cm)`}
                        value={heightDraft.cm}
                        onChange={(e) =>
                          setHeightDraft((prev) => ({
                            ...prev,
                            cm: e.target.value,
                          }))
                        }
                        className="flex-1 h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        placeholder="175"
                      />
                      <span
                        data-testid="height-unit"
                        className="flex items-center px-3 text-gray-600"
                      >
                        cm
                      </span>
                    </div>
                  )
                ) : (
                  <p className="text-gray-900 py-2">
                    {storedHeightCm(healthData.height) > 0
                      ? fmt.quantity(
                          "height",
                          storedHeightCm(healthData.height),
                        )
                      : t.notSpecified}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Body Data Section - moved directly after Basic Data */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2 whitespace-nowrap">
                <Activity className="w-5 h-5 text-red-600" />
                <span>{t.hrpBodyData}</span>
              </h2>
              <div className="flex items-center space-x-2">
                {lastWeightUpdate && (
                  <div className="text-xs text-gray-500 flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {t.lastUpdate}: {fmt.date(lastWeightUpdate)}
                    </span>
                  </div>
                )}
                <button
                  data-testid="edit-body-data"
                  onClick={() => toggleEditSection("koerperdaten")}
                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1"
                  title={editingSection === "koerperdaten" ? t.cancel : t.edit}
                >
                  <Edit3 className="w-3 h-3" />
                  <span>{t.edit}</span>
                </button>
                {editingSection === "koerperdaten" && (
                  <button
                    onClick={async () => {
                      if (!user?.id || !token) return;

                      try {
                        setSavingSections((prev) => ({
                          ...prev,
                          koerperdaten: true,
                        }));
                        setError(null);

                        // Save through the Body Data API (FHIR observations).
                        // The backend records the weight in kg whatever
                        // unit is sent, so the value typed in the display
                        // unit is converted here.
                        // Send only valid positive values to avoid backend validation failures.
                        const payload: {
                          weight?: number;
                          unit: "kg" | "lb";
                          height?: number;
                          heightUnit?: "cm" | "in";
                          bodyFat?: number;
                          bodyFatUnit?: "%" | "percent";
                        } = {
                          unit: "kg",
                        };

                        // An untouched draft sends no weight at all, rather
                        // than the stored value rounded through lb.
                        const enteredWeight =
                          weightDraft === weightDraftOpened
                            ? 0
                            : parseLocalizedNumber(weightDraft);
                        const weightKg =
                          enteredWeight > 0
                            ? Math.round(
                                fmt.fromDisplay("weight", enteredWeight) * 100,
                              ) / 100
                            : 0;
                        if (weightKg > 0) {
                          payload.weight = weightKg;
                        }

                        const heightCm = storedHeightCm(healthData.height);
                        if (heightCm > 0) {
                          payload.height = heightCm;
                          payload.heightUnit = "cm";
                        }

                        if (
                          healthData.bodyFat?.value &&
                          healthData.bodyFat.value > 0
                        ) {
                          payload.bodyFat = healthData.bodyFat.value;
                          payload.bodyFatUnit = "percent";
                        }

                        if (payload.weight == null && payload.bodyFat == null) {
                          setError(t.hrpErrorNoBodyValue);
                          return;
                        }

                        await bodyDataService.saveBodyData(payload);

                        if (payload.weight != null) {
                          setHealthData((prev) => ({
                            ...prev,
                            weight: { value: payload.weight!, unit: "kg" },
                          }));
                        }

                        // Reload the body data history after saving
                        await loadBodyDataHistory();

                        alert(t.hrpBodyDataSaved);
                        setEditingSection(null);
                      } catch (err: any) {
                        console.error("Body data save error:", err);

                        // Clearer messages for 400 validation errors
                        if (err.response?.status === 400) {
                          const validationErrors =
                            err.response?.data?.error?.details;
                          if (validationErrors && validationErrors.length > 0) {
                            const errorMessages = (
                              validationErrors as Array<{ msg: string }>
                            )
                              .map((e) => e.msg)
                              .join(", ");
                            setError(
                              `${t.hrpInputErrorPrefix}: ${errorMessages}`,
                            );
                          } else {
                            setError(t.hrpErrorInvalidBodyInput);
                          }
                        } else {
                          setError(t.hrpErrorSavingBodyData);
                        }
                      } finally {
                        setSavingSections((prev) => ({
                          ...prev,
                          koerperdaten: false,
                        }));
                      }
                    }}
                    data-testid="save-body-data"
                    disabled={savingSections.koerperdaten}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
                  >
                    {savingSections.koerperdaten ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    <span>
                      {savingSections.koerperdaten ? t.saving : t.save}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Current Data - Desktop Layout: Three Columns */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {/* Gewicht Section */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                  <Scale className="w-4 h-4" />
                  <span>{t.hrpWeightLastRecorded}</span>
                </div>
                {editingSection === "koerperdaten" ? (
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      name="weight"
                      data-testid="weight-input"
                      min="0"
                      step="0.1"
                      aria-label={`${t.weight} (${fmt.unit("weight")})`}
                      value={weightDraft}
                      onChange={(e) => setWeightDraft(e.target.value)}
                      className="w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder={
                        fmt.systemOf("weight") === "imperial" ? "155" : "70"
                      }
                    />
                    <span
                      data-testid="weight-unit"
                      className="flex items-center px-3 py-2 text-gray-600"
                    >
                      {fmt.unit("weight")}
                    </span>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      // Sort by timestamp to get the most recent entry
                      const sortedBodyData =
                        bodyDataHistory.length > 0
                          ? [...bodyDataHistory].sort(
                              (a, b) =>
                                new Date(b.timestamp).getTime() -
                                new Date(a.timestamp).getTime(),
                            )
                          : [];
                      const latestBodyData =
                        sortedBodyData.length > 0 ? sortedBodyData[0] : null;

                      const weightKg =
                        latestBodyData?.weight ||
                        storedWeightKg(healthData.weight);

                      return (
                        <>
                          <div className="flex items-center space-x-3">
                            <span
                              data-testid="weight-display"
                              className="text-2xl font-bold text-red-600"
                            >
                              {weightKg
                                ? fmt.number(
                                    fmt.toDisplay("weight", weightKg),
                                    1,
                                  )
                                : "-"}
                            </span>
                            <span className="text-gray-600">
                              {fmt.unit("weight")}
                            </span>
                          </div>
                          {latestBodyData && (
                            <div className="text-xs text-gray-500 mt-1">
                              {fmt.dateTime(latestBodyData.timestamp)}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* BMI Section */}
              {(() => {
                // Sort by timestamp to get the most recent entry for BMI as well
                const sortedBodyData =
                  bodyDataHistory.length > 0
                    ? [...bodyDataHistory].sort(
                        (a, b) =>
                          new Date(b.timestamp).getTime() -
                          new Date(a.timestamp).getTime(),
                      )
                    : [];
                const latestBodyData =
                  sortedBodyData.length > 0 ? sortedBodyData[0] : null;
                const bmiValue = latestBodyData?.bmi || healthData.bmi;

                return bmiValue ? (
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Calculator className="w-4 h-4" />
                      <span>{t.hrpBmiLastRecorded}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-gray-900">
                        {fmt.number(bmiValue, 1)}
                      </span>
                      <span
                        className={`text-sm font-medium px-2 py-1 rounded-full ${getBMICategory(bmiValue).color} bg-opacity-10`}
                      >
                        {getBMICategory(bmiValue).category}
                      </span>
                    </div>
                    {latestBodyData && (
                      <div className="text-xs text-gray-500 mt-1">
                        {fmt.dateTime(latestBodyData.timestamp)}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Body Fat Section */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                  <Utensils className="w-4 h-4" />
                  <span>{t.hrpBodyFatLastRecorded}</span>
                </label>
                {editingSection === "koerperdaten" ? (
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={healthData.bodyFat?.value || ""}
                      onChange={(e) =>
                        setHealthData((prev) => ({
                          ...prev,
                          bodyFat: {
                            value: parseLocalizedNumber(e.target.value),
                            unit: "%",
                          },
                        }))
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      placeholder="15.5"
                    />
                    <span className="flex items-center px-3 py-2 text-gray-600">
                      %
                    </span>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      // Sort by timestamp to get the most recent entry
                      const sortedBodyData =
                        bodyDataHistory.length > 0
                          ? [...bodyDataHistory].sort(
                              (a, b) =>
                                new Date(b.timestamp).getTime() -
                                new Date(a.timestamp).getTime(),
                            )
                          : [];
                      const latestBodyData =
                        sortedBodyData.length > 0 ? sortedBodyData[0] : null;
                      const bodyFatValue =
                        latestBodyData?.bodyFat || healthData.bodyFat?.value;

                      return bodyFatValue ? (
                        <>
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl font-bold text-orange-600">
                              {fmt.number(bodyFatValue, 1)}
                            </span>
                            <span className="text-gray-600">%</span>
                          </div>
                          {latestBodyData && (
                            <div className="text-xs text-gray-500 mt-1">
                              {fmt.dateTime(latestBodyData.timestamp)}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-gray-400 text-sm">
                          {t.hrpNoDataRecorded}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Chart Section - Full Width at Bottom */}
            {/* The testid sits here rather than on BodyDataChart so it is
                present in the empty state too - the chart is not mounted at
                all when there is no weight data. */}
            <div data-testid="body-data-chart" className="mt-6">
              <div
                role="group"
                aria-label={t.hrpChartRangeLabel}
                className="flex flex-wrap gap-2 mb-4"
              >
                {CHART_RANGES.map((range) => (
                  <button
                    key={range.value}
                    type="button"
                    data-testid={`time-filter-${range.value}`}
                    aria-pressed={chartRange === range.value}
                    onClick={() => setChartRange(range.value)}
                    className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                      chartRange === range.value
                        ? "bg-sky-600 border-sky-600 text-white"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t[range.labelKey]}
                  </button>
                ))}
              </div>
              {(() => {
                const rangedHistory = filterByRange(
                  bodyDataHistory,
                  chartRange,
                );
                const weightChartData = rangedHistory.filter(
                  (entry) => typeof entry.weight === "number",
                );
                const bodyFatChartData = rangedHistory.filter(
                  (entry) => typeof entry.bodyFat === "number",
                );

                return (
                  <>
                    <div className="flex items-center space-x-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-gray-600" />
                      <h4 className="text-lg font-medium text-gray-900">
                        {t.hrpWeightHistory.replace(
                          "{count}",
                          String(weightChartData.length),
                        )}
                      </h4>
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>

                    {weightChartData.length > 0 ? (
                      <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
                        <BodyDataChart
                          data={weightChartData}
                          showBMI={!!healthData.height.value}
                          showBodyFat={false}
                          height={280}
                        />
                      </div>
                    ) : (
                      <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>{t.hrpNoWeightChartData}</p>
                          <p className="text-sm">{t.hrpFirstEntriesAppear}</p>

                          {/* Quick FHIR Test Data Button */}
                          <button
                            onClick={async () => {
                              if (!user?.id || !token) return;

                              try {
                                log.debug(
                                  "🔬 Creating FHIR R5 Body Weight Observations...",
                                );

                                // Create FHIR-compliant test body weight data
                                const testData = [
                                  {
                                    weight: 73.5,
                                    unit: "kg" as const,
                                    height: 180,
                                    heightUnit: "cm" as const,
                                  },
                                  {
                                    weight: 73.2,
                                    unit: "kg" as const,
                                    height: 180,
                                    heightUnit: "cm" as const,
                                  },
                                  {
                                    weight: 73.8,
                                    unit: "kg" as const,
                                    height: 180,
                                    heightUnit: "cm" as const,
                                  },
                                  {
                                    weight: 73.0,
                                    unit: "kg" as const,
                                    height: 180,
                                    heightUnit: "cm" as const,
                                  },
                                  {
                                    weight: 72.7,
                                    unit: "kg" as const,
                                    height: 180,
                                    heightUnit: "cm" as const,
                                  },
                                ];

                                for (const data of testData) {
                                  await bodyDataService.saveBodyData(data);
                                  log.debug(
                                    `✅ FHIR Observation created: ${data.weight}kg`,
                                  );
                                  await new Promise((resolve) =>
                                    setTimeout(resolve, 300),
                                  ); // Small delay
                                }

                                log.debug(
                                  "🎉 FHIR Body Weight Observations created in fHIRResource table",
                                );

                                // Reload the body data history from FHIR database
                                await loadBodyDataHistory();
                              } catch (error: any) {
                                console.error(
                                  "❌ Error creating FHIR test data:",
                                  error,
                                );
                                setError(t.hrpErrorTestData);
                              }
                            }}
                            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                          >
                            {t.hrpCreateTestData}
                          </button>
                          <p className="text-xs text-gray-400 mt-2">
                            {t.hrpCreateTestDataHint}
                          </p>
                        </div>
                      </div>
                    )}

                    {bodyFatChartData.length > 0 && (
                      <div className="mt-8">
                        <div className="flex items-center space-x-2 mb-4">
                          <Utensils className="w-5 h-5 text-orange-500" />
                          <h4 className="text-lg font-medium text-gray-900">
                            {t.hrpBodyFatHistory.replace(
                              "{count}",
                              String(bodyFatChartData.length),
                            )}
                          </h4>
                          <Calendar className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
                          <BodyDataChart
                            data={bodyFatChartData}
                            showBMI={false}
                            showBodyFat={true}
                            height={280}
                          />
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Unified FHIR Vital Signs Box */}
          <VitalSignsBox
            mode="health-records"
            initialData={{
              systolicBloodPressure: healthData.bloodPressure?.systolic ?? 0,
              diastolicBloodPressure: healthData.bloodPressure?.diastolic ?? 0,
              bloodPressureUnit: "mmHg",
              restingHeartRate: healthData.restingHeartRate?.value ?? 0,
              heartRateUnit: "bpm",
              recordedAt:
                healthData.bloodPressure?.recordedAt ??
                new Date().toISOString(),
              fhirStatus: "final",
            }}
            onSave={async (vitalSignsData) => {
              try {
                // Update local state with unified data structure
                setHealthData((prev) => ({
                  ...prev,
                  // The service reports an absent measurement as undefined
                  // rather than a synthetic default, so keep whatever was
                  // already recorded instead of overwriting it with nothing.
                  bloodPressure: {
                    systolic:
                      vitalSignsData.systolicBloodPressure ??
                      prev.bloodPressure?.systolic ??
                      0,
                    diastolic:
                      vitalSignsData.diastolicBloodPressure ??
                      prev.bloodPressure?.diastolic ??
                      0,
                    unit: "mmHg",
                    recordedAt: vitalSignsData.recordedAt,
                  },
                  restingHeartRate: {
                    value:
                      vitalSignsData.restingHeartRate ??
                      prev.restingHeartRate?.value ??
                      0,
                    unit: "bpm",
                    recordedAt: vitalSignsData.recordedAt,
                  },
                }));

                log.debug("✅ Unified vital signs saved successfully");

                // Reload health data to get latest values from FHIR API
                await loadHealthData();
              } catch (error: any) {
                console.error("❌ Error saving unified vital signs:", error);
                setError(t.hrpErrorSavingVitals);
              }
            }}
            onError={(message) => {
              setError(message);
            }}
            showChart={true}
            showEditButton={true}
            showHeader={true}
            className="mb-6"
          />

          {/* Phase 1: Critical Travel Health Data */}

          {/* Lab Results Component */}
          <LabResults
            labResults={healthData.labResults}
            onSave={async (labResults) => {
              try {
                // Set saving state
                setSavingSections((prev) => ({ ...prev, labResults: true }));

                // Save to FHIR database
                const result =
                  await labResultsService.saveLabResults(labResults);
                if (result.success) {
                  // Load fresh lab results from FHIR database
                  const labResultsResponse =
                    await labResultsService.getLabResults();
                  if (labResultsResponse.success) {
                    log.debug(
                      "✅ Lab results refreshed after save:",
                      labResultsResponse.data,
                    );
                    setHealthData((prev) => ({
                      ...prev,
                      labResults: labResultsResponse.data || [],
                    }));
                  } else {
                    // Fallback: use the saved data
                    setHealthData((prev) => ({
                      ...prev,
                      labResults,
                    }));
                  }

                  // Exit edit mode after successful save
                  setEditingSection(null);

                  // Show success message in current language
                  i18nToast.labResultsSaved();

                  // Reload general health data
                  await loadHealthData();
                }
              } catch (error: any) {
                console.error("❌ Error saving lab results:", error);
                setError(t.hrpErrorSavingLabResults);
              } finally {
                // Clear saving state
                setSavingSections((prev) => ({ ...prev, labResults: false }));
              }
            }}
            onFileUpload={async (file, labResultId) => {
              try {
                const uploadResult = await labResultsService.uploadLabReport(
                  file,
                  labResultId,
                );
                if (!uploadResult.success) {
                  setError(
                    typeof uploadResult.error === "string"
                      ? uploadResult.error
                      : t.hrpErrorUploadingLabReport,
                  );
                  return;
                }
                // Refresh list to ensure uploaded report-linked results are current.
                await loadLabResults();
              } catch (uploadError: any) {
                console.error("❌ Error uploading lab report:", uploadError);
                setError(t.hrpErrorUploadingLabReport);
              }
            }}
            isEditing={editingSection === "labResults"}
            onToggleEdit={() => toggleEditSection("labResults")}
            isSaving={savingSections.labResults || false}
          />

          {/* Extended Allergies Component */}
          <ExtendedAllergies
            allergies={healthData.extendedAllergies}
            onSave={async (extendedAllergies) => {
              const updatedHealthData = {
                ...healthData,
                extendedAllergies,
              };
              setHealthData(updatedHealthData);
              await handleSaveSection(
                "extendedAllergies",
                t.allergiesAndIntolerances,
                updatedHealthData,
              );
            }}
            isEditing={editingSection === "extendedAllergies"}
            onToggleEdit={() => toggleEditSection("extendedAllergies")}
            isSaving={savingSections.extendedAllergies || false}
          />

          {/* Phase 2: Medication & Treatment History */}

          {/* Medication Management Component - Hidden: Available on separate /medications page */}
          {/*
          <MedicationManagement
            medications={healthData.currentMedications || []}
            onSave={async (currentMedications) => {
              setHealthData(prev => ({
                ...prev,
                currentMedications
              }));
              await loadHealthData();
            }}
            isEditing={editingSection === 'currentMedications'}
            onToggleEdit={() => toggleEditSection('currentMedications')}
            isSaving={savingSections.currentMedications || false}
          />
          */}

          {/* Medical Conditions Component */}
          <MedicalConditions
            conditions={healthData.medicalConditions || []}
            onSave={async (medicalConditions) => {
              setHealthData((prev) => ({
                ...prev,
                medicalConditions,
              }));
              await loadHealthData();
            }}
            isEditing={editingSection === "medicalConditions"}
            onToggleEdit={() => toggleEditSection("medicalConditions")}
            isSaving={savingSections.medicalConditions || false}
          />

          {/* Medical Procedures Component */}
          <MedicalProceduresComponent
            procedures={healthData.medicalProcedures || []}
            onSave={async (medicalProcedures) => {
              setHealthData((prev) => ({
                ...prev,
                medicalProcedures,
              }));
              await loadHealthData();
            }}
            isEditing={editingSection === "medicalProcedures"}
            onToggleEdit={() => toggleEditSection("medicalProcedures")}
            isSaving={savingSections.medicalProcedures || false}
          />

          {/* Treatment History Component */}
          <TreatmentHistoryComponent
            treatments={healthData.treatmentHistory || []}
            onSave={async (treatmentHistory) => {
              setHealthData((prev) => ({
                ...prev,
                treatmentHistory,
              }));
              await loadHealthData();
            }}
            isEditing={editingSection === "treatmentHistory"}
            onToggleEdit={() => toggleEditSection("treatmentHistory")}
            isSaving={savingSections.treatmentHistory || false}
          />

          {/* ✅ NEW: Physical Wellbeing - Separate Box */}
          <PhysicalWellbeingBox
            physicalWellbeing={healthData.physicalWellbeing}
            onPhysicalChange={(value) =>
              setHealthData((prev) => ({
                ...prev,
                physicalWellbeing: value as 1 | 2 | 3 | 4 | 5,
              }))
            }
            onSave={handleSavePhysicalWellbeing}
            lastRecordedAt={healthData.wellbeingRecorded}
            wellbeingHistory={wellbeingHistory}
          />

          {/* ✅ NEW: Emotional Wellbeing - Separate Box */}
          <EmotionalWellbeingBox
            emotionalWellbeing={healthData.emotionalWellbeing}
            onEmotionalChange={(value) =>
              setHealthData((prev) => ({
                ...prev,
                emotionalWellbeing: value as 1 | 2 | 3 | 4 | 5,
              }))
            }
            onSave={handleSaveEmotionalWellbeing}
            lastRecordedAt={healthData.wellbeingRecorded}
            wellbeingHistory={wellbeingHistory}
          />

          {/* Medical Incidents */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <span>{t.medicalIncidents}</span>
            </h2>

            {/* Add New Incident */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-4">
                {t.addNewIncident}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.date}
                  </label>
                  <input
                    type="date"
                    value={newIncident.date}
                    onChange={(e) =>
                      setNewIncident((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.icdCodeOptional}
                  </label>
                  <input
                    type="text"
                    value={newIncident.icdCode}
                    onChange={(e) =>
                      setNewIncident((prev) => ({
                        ...prev,
                        icdCode: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder={t.hrpIcdPlaceholder}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.diagnosisDesignation}
                  </label>
                  <input
                    type="text"
                    value={newIncident.diagnosis}
                    onChange={(e) =>
                      setNewIncident((prev) => ({
                        ...prev,
                        diagnosis: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder={t.diagnosisPlaceholder}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.description}
                  </label>
                  <textarea
                    value={newIncident.description}
                    onChange={(e) =>
                      setNewIncident((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder={t.incidentDetailsPlaceholder}
                  />
                </div>
              </div>
              <button
                onClick={addMedicalIncident}
                disabled={!newIncident.date || !newIncident.diagnosis}
                className="mt-4 flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t.addIncident}</span>
              </button>
            </div>

            {/* Existing Incidents */}
            <div className="space-y-3">
              {healthData.medicalIncidents.map((incident) => (
                <div
                  key={incident.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {fmt.date(incident.date)}
                        </span>
                        {incident.icdCode && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {incident.icdCode}
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-gray-900">
                        {incident.diagnosis}
                      </h4>
                      {incident.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {incident.description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeMedicalIncident(incident.id!)}
                      className="text-red-500 hover:text-red-700 p-1"
                      aria-label={t.delete}
                      title={t.delete}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {healthData.medicalIncidents.length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  {t.noMedicalIncidentsDocumented}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Now displayed vertically below main content */}
        <div className="space-y-6">
          {/* Allergies and Intolerances */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                <Utensils className="w-5 h-5 text-orange-600" />
                <span>{t.intolerances}</span>
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleEditSection("unvertraeglichkeiten")}
                  className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                  title={
                    editingSection === "unvertraeglichkeiten"
                      ? t.cancel
                      : t.edit
                  }
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                {editingSection === "unvertraeglichkeiten" && (
                  <button
                    onClick={() =>
                      handleSaveSection("unvertraeglichkeiten", t.intolerances)
                    }
                    disabled={savingSections.unvertraeglichkeiten}
                    className="px-3 py-1 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
                  >
                    {savingSections.unvertraeglichkeiten ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    <span>
                      {savingSections.unvertraeglichkeiten ? t.saving : t.save}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Food Intolerances */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.foodIntolerances}
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newFoodIntolerance}
                  onChange={(e) => setNewFoodIntolerance(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  placeholder={t.foodIntolerancesPlaceholder}
                />
                <button
                  onClick={addFoodIntolerance}
                  disabled={!newFoodIntolerance.trim()}
                  className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1">
                {healthData.foodIntolerances.map((food, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-orange-50 text-orange-800 px-3 py-2 rounded-lg text-sm"
                  >
                    <span>{food}</span>
                    <button
                      onClick={() => removeFoodIntolerance(index)}
                      className="text-orange-600 hover:text-orange-800"
                      aria-label={t.delete}
                      title={t.delete}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Specific Conditions */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                <Pill className="w-5 h-5 text-red-600" />
                <span>{t.specificDiseases}</span>
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleEditSection("erkrankungen")}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={editingSection === "erkrankungen" ? t.cancel : t.edit}
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                {editingSection === "erkrankungen" && (
                  <button
                    onClick={() =>
                      handleSaveSection("erkrankungen", t.specificDiseases)
                    }
                    disabled={savingSections.erkrankungen}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
                  >
                    {savingSections.erkrankungen ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    <span>
                      {savingSections.erkrankungen ? t.saving : t.save}
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={healthData.coagulationDisorder}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        coagulationDisorder: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t.coagulationDisorder}
                  </span>
                </label>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={healthData.heartAttack.hasHad}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        heartAttack: {
                          ...prev.heartAttack,
                          hasHad: e.target.checked,
                        },
                      }))
                    }
                    className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t.heartAttack}
                  </span>
                </label>
                {healthData.heartAttack.hasHad && (
                  <input
                    type="date"
                    value={healthData.heartAttack.date || ""}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        heartAttack: {
                          ...prev.heartAttack,
                          date: e.target.value,
                        },
                      }))
                    }
                    className="ml-7 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                    placeholder={t.heartAttackDatePlaceholder}
                  />
                )}
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={healthData.stroke.hasHad}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        stroke: { ...prev.stroke, hasHad: e.target.checked },
                      }))
                    }
                    className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t.stroke}
                  </span>
                </label>
                {healthData.stroke.hasHad && (
                  <input
                    type="date"
                    value={healthData.stroke.date || ""}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        stroke: { ...prev.stroke, date: e.target.value },
                      }))
                    }
                    className="ml-7 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                    placeholder={t.strokeDatePlaceholder}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Disabilities */}
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-purple-600" />
                <span>{t.hrpDisabilities}</span>
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleEditSection("beeintraechtigungen")}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title={
                    editingSection === "beeintraechtigungen" ? t.cancel : t.edit
                  }
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                {editingSection === "beeintraechtigungen" && (
                  <button
                    onClick={() =>
                      handleSaveSection(
                        "beeintraechtigungen",
                        t.hrpDisabilities,
                      )
                    }
                    disabled={savingSections.beeintraechtigungen}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
                  >
                    {savingSections.beeintraechtigungen ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-3 h-3" />
                    )}
                    <span>
                      {savingSections.beeintraechtigungen ? t.saving : t.save}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Physical Disability */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">
                {t.hrpPhysicalDisability}
              </h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={healthData.physicalDisability?.type || ""}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      physicalDisability: {
                        ...prev.physicalDisability,
                        type: e.target.value,
                        severity: prev.physicalDisability?.severity || "mild",
                        travelSupport:
                          prev.physicalDisability?.travelSupport || "",
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  placeholder={t.hrpDisabilityType}
                />
                <select
                  value={healthData.physicalDisability?.severity || "mild"}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      physicalDisability: {
                        ...prev.physicalDisability,
                        type: prev.physicalDisability?.type || "",
                        severity: e.target.value as
                          | "mild"
                          | "moderate"
                          | "severe",
                        travelSupport:
                          prev.physicalDisability?.travelSupport || "",
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                >
                  <option value="mild">{t.hrpSeverityMild}</option>
                  <option value="moderate">{t.hrpSeverityModerate}</option>
                  <option value="severe">{t.hrpSeveritySevere}</option>
                </select>
                <textarea
                  value={healthData.physicalDisability?.travelSupport || ""}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      physicalDisability: {
                        ...prev.physicalDisability,
                        type: prev.physicalDisability?.type || "",
                        severity: prev.physicalDisability?.severity || "mild",
                        travelSupport: e.target.value,
                      },
                    }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  placeholder={t.hrpTravelSupportNeeds}
                />
              </div>
            </div>

            {/* Mental Disability */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">
                {t.hrpCognitiveDisability}
              </h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={healthData.mentalDisability?.type || ""}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      mentalDisability: {
                        ...prev.mentalDisability,
                        type: e.target.value,
                        severity: prev.mentalDisability?.severity || "mild",
                        travelSupport:
                          prev.mentalDisability?.travelSupport || "",
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  placeholder={t.hrpDisabilityType}
                />
                <select
                  value={healthData.mentalDisability?.severity || "mild"}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      mentalDisability: {
                        ...prev.mentalDisability,
                        type: prev.mentalDisability?.type || "",
                        severity: e.target.value as
                          | "mild"
                          | "moderate"
                          | "severe",
                        travelSupport:
                          prev.mentalDisability?.travelSupport || "",
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                >
                  <option value="mild">{t.hrpSeverityMild}</option>
                  <option value="moderate">{t.hrpSeverityModerate}</option>
                  <option value="severe">{t.hrpSeveritySevere}</option>
                </select>
                <textarea
                  value={healthData.mentalDisability?.travelSupport || ""}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      mentalDisability: {
                        ...prev.mentalDisability,
                        type: prev.mentalDisability?.type || "",
                        severity: prev.mentalDisability?.severity || "mild",
                        travelSupport: e.target.value,
                      },
                    }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  placeholder={t.hrpTravelSupportNeeds}
                />
              </div>
            </div>
          </div>

          {/* Data Security Info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="text-green-800 font-medium text-sm">
                  {t.hrpSecurityTitle}
                </h3>
                <p className="text-green-700 text-xs mt-1">
                  {t.hrpSecurityText}
                </p>
                <p className="text-green-700 text-xs mt-1">
                  <strong>{t.hrpDataDeletionLabel}:</strong>{" "}
                  {t.hrpDataDeletionText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Apple Health / Google Fit Import */}
      <HealthEcosystemImportBox
        onImported={async () => {
          await loadHealthData();
          await loadBodyDataHistory();
        }}
      />

      {/* Global Actions */}
      <div className="flex justify-center">
        <button
          onClick={() => alert(t.hrpConsentComingSoon)}
          className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Shield className="w-4 h-4" />
          <span>{t.hrpManageConsents}</span>
        </button>
      </div>
    </div>
  );
};
